import { Button, Popconfirm, Select, message } from 'antd';
import { CopyOutlined } from '@ant-design/icons';
import { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { serverFetch, fileUrl, apiLink } from '@server';
import { showError } from '@extensions/utils';
import { formatPhone } from '@extensions/contact-mask';
import { isMissedCall } from './dialog-utils';
import { useDicts } from '@extensions/dicts';

const RECORD_PENDING = 1;
const RECORD_DONE = 3;
const RECORD_TRANSCRIBING = 4;
const RECORD_ERROR = -1;

const sleep = (ms) => new Promise((resolve) => { setTimeout(resolve, ms); });

const speakerOrder = (segments) => {
    const speakers = [];
    segments.forEach((item) => {
        if (!speakers.includes(item.speaker)) speakers.push(item.speaker);
    });
    return speakers;
};

const readRoles = (raw) => {
    if (!raw) return {};
    if (typeof raw === 'object') return raw;
    try {
        return JSON.parse(raw) ?? {};
    } catch {
        return {};
    }
};

const roleOfSpeaker = (speaker, speakers, call) => {
    const saved = readRoles(call.roles);
    if (saved[speaker]) return saved[speaker];
    const employee = speakers.indexOf(speaker) <= 0;
    return (call.swapped ? !employee : employee) ? 'employee' : 'client';
};

const titleOfRole = (role, call, users) => {
    if (role === 'client') return call.contragentName || 'Клиент';
    if (role?.startsWith('user:')) {
        const id = Number(role.slice(5));
        return users.find((user) => user.id === id)?.name || 'Сотрудник';
    }
    return 'Сотрудник';
};

// Расшифровка звонка: роли спикеров и кто это
const CallCard = ({ call, onChanged }) => {
    const { dicts } = useDicts();
    const user = useSelector((s) => s.user);
    const [contragents, setContragents] = useState([]);
    const [starting, setStarting] = useState(false);
    const [fetching, setFetching] = useState(false);
    const watchId = useRef(0);
    let segments = [];
    try {
        segments = call.transcript ? JSON.parse(call.transcript) : [];
    } catch {
        segments = [];
    }

    useEffect(() => { setStarting(false); }, [call.recordStatus, call.transcript]);
    useEffect(() => () => { watchId.current += 1; }, []);

    useEffect(() => {
        if (!call.contragentId) {
            setContragents([]);
            return;
        }
        setContragents([{ value: call.contragentId, label: call.contragentName || `#${call.contragentId}` }]);
    }, [call.contragentId, call.contragentName]);

    const savePersons = (patch) => {
        serverFetch(`call/${call.id}/persons`, {
            method: 'PUT',
            bodyData: {
                userId: patch.userId === undefined ? call.userId : patch.userId,
                contragentId: patch.contragentId === undefined ? call.contragentId : patch.contragentId,
            },
        }).then(onChanged).catch(showError);
    };

    const transcribing = starting || call.recordStatus === RECORD_TRANSCRIBING;

    const reportCall = (fresh) => {
        if (!fresh) {
            message.warning('Не удалось получить результат расшифровки');
            return;
        }
        if (fresh.recordStatus === RECORD_ERROR) message.error(fresh.note || 'Не удалось расшифровать');
        else if (fresh.recordStatus === RECORD_DONE || fresh.transcript) message.success('Звонок расшифрован');
        else message.warning(fresh.note || 'Расшифровка не выполнена');
    };

    // Расшифровка идёт в фоне: опрашиваем звонок, пока статус не сменится
    const watchCall = async (gen) => {
        let delay = 1000;
        const deadline = Date.now() + 10 * 60 * 1000;
        while (watchId.current === gen && Date.now() < deadline) {
            await sleep(delay);
            delay = 2000;
            if (watchId.current !== gen) return;
            let list;
            try {
                list = await serverFetch(`call?dialogId=${call.dialogId}`);
            } catch (e) {
                if (watchId.current !== gen) return;
                showError(e);
                setStarting(false);
                return;
            }
            const fresh = (list ?? []).find((item) => item.id === call.id);
            if (fresh && fresh.recordStatus === RECORD_TRANSCRIBING) continue;
            if (watchId.current !== gen) return;
            reportCall(fresh);
            onChanged?.(fresh);
            setStarting(false);
            return;
        }
        if (watchId.current === gen) {
            message.warning('Расшифровка не завершилась');
            setStarting(false);
        }
    };

    const onTranscribe = () => {
        if (starting) return;
        setStarting(true);
        const gen = watchId.current + 1;
        watchId.current = gen;
        serverFetch(`call/${call.id}/transcribe`, { method: 'POST' })
            .then((updated) => {
                onChanged?.(updated);
                return watchCall(gen);
            })
            .catch((e) => {
                if (watchId.current === gen) watchId.current += 1;
                showError(e);
                setStarting(false);
            });
    };

    const onFetchRecord = () => {
        setFetching(true);
        serverFetch(`call/${call.id}/fetch-record`, { method: 'POST' })
            .then((updated) => {
                onChanged?.(updated);
                if (updated?.fileId) message.success('Запись получена');
                else message.warning(updated?.note || 'Запись ещё не найдена');
            })
            .catch(showError)
            .finally(() => setFetching(false));
    };

    const speakers = speakerOrder(segments);
    const roleOptions = [
        { value: 'employee', label: 'Сотрудник' },
        { value: 'client', label: call.contragentName ? `Клиент · ${call.contragentName}` : 'Клиент' },
        ...dicts.users.map((user) => ({ value: `user:${user.id}`, label: user.name })),
    ];
    const saveRoles = (speaker, value) => {
        const roles = {};
        speakers.forEach((id) => {
            roles[id] = id === speaker ? value : roleOfSpeaker(id, speakers, call);
        });
        serverFetch(`call/${call.id}/roles`, { method: 'PUT', bodyData: { roles } })
            .then(onChanged)
            .catch(showError);
    };

    const onSearch = (text) => {
        if (!text || text.length < 2) return;
        serverFetch(`contragent/search?name=${encodeURIComponent(text)}&limit=20`)
            .then((list) => setContragents((list ?? []).map((item) => ({ value: item.id, label: item.name }))))
            .catch(showError);
    };

    return (
        <div className="call-card">
            <div className="call-card-persons">
                <Select
                    allowClear
                    showSearch
                    optionFilterProp="label"
                    placeholder="Сотрудник"
                    size="small"
                    style={{ minWidth: 140, flex: 1 }}
                    value={call.userId ?? undefined}
                    options={dicts.users.map((user) => ({ value: user.id, label: user.name }))}
                    onChange={(value) => savePersons({ userId: value ?? null })}
                />
                <Select
                    allowClear
                    showSearch
                    filterOption={false}
                    placeholder="Клиент"
                    size="small"
                    style={{ minWidth: 140, flex: 1 }}
                    value={call.contragentId ?? undefined}
                    options={contragents}
                    onSearch={onSearch}
                    onChange={(value) => savePersons({ contragentId: value ?? null })}
                />
            </div>
            {call.line ? <div className="call-line">Линия: {formatPhone(call.line)}</div> : null}
            {isMissedCall(call) ? <div className="call-missed">Пропущенный</div> : null}
            {!call.fileId && call.duration > 0 && (
                <div className="call-record">
                    {call.recordStatus === RECORD_PENDING && !call.note?.startsWith('Билайн')
                        ? <span className="card-list-muted">{call.note || 'Ожидание записи из АТС…'}</span>
                        : <span className="dlg-msg-error">{call.note || 'Записи нет'}</span>}
                    <Button size="small" type="link" loading={fetching} disabled={fetching} onClick={onFetchRecord}>
                        Проверить запись
                    </Button>
                </div>
            )}
            {call.fileId && segments.length === 0 && (
                <Button size="small" type="link" loading={transcribing} disabled={transcribing} onClick={onTranscribe}>
                    {transcribing ? 'Расшифровка…' : 'Расшифровать'}
                </Button>
            )}
            {call.fileId && call.recordStatus === RECORD_ERROR && call.note
                ? <div className="dlg-msg-error">{call.note}</div>
                : null}
            {user?.isSuperadmin && (
                <div className="call-record card-list-muted" title="id звонка · extTrackingId · абонент АТС">
                    <span>#{call.id} · {call.externalId || '—'} · {call.abonentExternalId || '—'}</span>
                    <Button
                        size="small" type="text" icon={<CopyOutlined />} aria-label="Копировать"
                        onClick={() => {
                            navigator.clipboard?.writeText(`${call.externalId} ${call.abonentExternalId}`);
                            message.success('Скопировано');
                        }}
                    />
                    <a
                        href={call.fileId ? fileUrl(call.fileId) : apiLink(`call/${call.id}/record`)}
                        title={call.fileId ? 'Скачать сохранённую запись' : 'Скачать запись напрямую из АТС'}
                    >
                        Скачать запись
                    </a>
                </div>
            )}
            {segments.length > 0 && (
                <>
                    <Popconfirm
                        title="Расшифровать звонок ещё раз?"
                        okText="Расшифровать"
                        cancelText="Отмена"
                        onConfirm={onTranscribe}
                    >
                        <Button size="small" type="link" loading={transcribing} disabled={transcribing}>
                            {transcribing ? 'Расшифровка…' : 'Расшифровать заново'}
                        </Button>
                    </Popconfirm>
                    {speakers.map((speaker) => {
                        const sample = segments.find((item) => item.speaker === speaker)?.text ?? '';
                        const short = sample.length > 42 ? `${sample.slice(0, 42)}…` : sample;
                        return (
                            <div key={speaker} className="call-speaker">
                                <span className="call-speaker-sample" title={sample}>{short}</span>
                                <Select
                                    size="small"
                                    showSearch
                                    optionFilterProp="label"
                                    disabled={transcribing}
                                    value={roleOfSpeaker(speaker, speakers, call)}
                                    options={roleOptions}
                                    onChange={(value) => saveRoles(speaker, value)}
                                />
                            </div>
                        );
                    })}
                    {segments.map((segment, index) => (
                        <div key={`${segment.startMs}-${index}`} className="call-line">
                            <b>{titleOfRole(roleOfSpeaker(segment.speaker, speakers, call), call, dicts.users)}: </b>
                            {segment.text}
                        </div>
                    ))}
                </>
            )}
        </div>
    );
};

export default CallCard;
